/** Display helpers. Mirrors the backend formatting so both agree. */

export function formatSwimTime(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '--:--';
  const totalHundredths = Math.round(ms / 10);
  const hundredths = totalHundredths % 100;
  const totalSeconds = Math.floor(totalHundredths / 100);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);
  const pad = (n: number) => String(n).padStart(2, '0');
  const tail = `${pad(seconds)}.${pad(hundredths)}`;
  return hours > 0 ? `${hours}:${pad(minutes)}:${tail}` : `${pad(minutes)}:${tail}`;
}

/** Drops the hundredths, which is the right precision for a predicted time. */
export function formatSwimTimeShort(ms: number): string {
  const full = formatSwimTime(ms);
  return full.replace(/\.\d{2}$/, '');
}

export function formatMoney(pence: number, currency = 'GBP'): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(pence / 100);
}

export function formatDate(iso: string): string {
  const date = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

export function formatDateShort(iso: string): string {
  const date = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(date);
}

/** Clock time only, for heat start times. */
export function formatClock(iso: string | null): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(date);
}

/** "in 3 days" / "2 hours ago" for the countdown and audit columns. */
export function formatRelative(iso: string): string {
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return iso;
  const diffSeconds = Math.round((target - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' });

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3600],
    ['minute', 60],
  ];
  for (const [unit, seconds] of units) {
    if (Math.abs(diffSeconds) >= seconds) {
      return rtf.format(Math.round(diffSeconds / seconds), unit);
    }
  }
  return rtf.format(diffSeconds, 'second');
}

/** Converts a datetime-local input value to an ISO string, and back. */
export function isoToLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  // Interpreted as UTC throughout so the value the admin sees matches storage.
  return date.toISOString().slice(0, 16);
}

export function localInputToIso(value: string): string {
  if (!value) return '';
  const date = new Date(`${value}:00Z`);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  waitlisted: 'Waitlisted',
  withdrawn: 'Withdrawn',
  dns: 'Did not start',
  dnf: 'Did not finish',
  dsq: 'Disqualified',
  unpaid: 'Unpaid',
  paid: 'Paid',
  refunded: 'Refunded',
  waived: 'Waived',
  female: 'Female',
  male: 'Male',
  other: 'Other',
  prefer_not_to_say: 'Prefer not to say',
};

export function labelFor(value: string): string {
  return STATUS_LABELS[value] ?? value;
}
